import get from 'lodash/get';
import has from 'lodash/has';
import capitalize from 'lodash/capitalize';
import * as stateChange from './state-change.js';
import { fireWebGuidePublishTrackEvents } from './web-guide-publish/web-guide-publish.js';
import { APPS } from '@pendo/services/Constants';
import { isIdeaPollGuide } from '@/stores/helpers/guides.helper';
import {
    IDEA_TEST_PUBLISHED,
    EMBEDDED_POLL_GUIDE_PUBLISHED,
    PUBLIC_GUIDE_CONTROLLED_ROLLOUT,
} from '@/guides/track-events/track-events-types';
import { isEmbeddedGuide, getVisiblePolls } from '@/guides/helpers/common.util';
import isEqual from 'lodash/isEqual';

export function fireGuideTrackEvents (updatedGuide, remoteGuide, numOfVisitorsInSegment, app, guideCategoryName) {
    const toState = get(updatedGuide, 'state', false);
    const fromState = get(remoteGuide, 'state', false);

    const rolloutUpdated = !isEqual(updatedGuide?.rollout, remoteGuide?.rollout);

    if (toState === 'public' && toState === fromState && rolloutUpdated) {
        trackPublicGuideControlledRollout(updatedGuide, false);
    }

    if (!toState || !remoteGuide || toState === fromState) {
        return;
    }

    stateChange.trackGuideStateChange(toState, fromState, remoteGuide, 'guide-status-change');

    const experimentDefined = has(updatedGuide, 'conversion.itemType') && has(updatedGuide, 'experiment');

    if (toState === 'public') {
        if (app.platform === APPS.WEB_PLATFORM || app.platform === APPS.EXTENSION_PLATFORM) {
            fireWebGuidePublishTrackEvents(updatedGuide, app.platform, numOfVisitorsInSegment, guideCategoryName);
        }
        if (isIdeaPollGuide(updatedGuide)) {
            stateChange.trackIdeaPollPublish(updatedGuide);
        }

        if (experimentDefined) {
            stateChange.trackGuideExperimentStarted(updatedGuide, 'guide-experiment-started');
        }

        if (isEmbeddedGuide(updatedGuide) && getVisiblePolls(updatedGuide).length > 0) {
            stateChange.trackEmbeddedPollGuidePublished(updatedGuide);
        }

        if (updatedGuide?.rollout) {
            trackPublicGuideControlledRollout(updatedGuide, true);
        }
    }
}

export function trackGuideStateChange (toState, fromState, guide, name) {
    const { appId, createdAt, id, showsAfter, lastUpdatedAt, steps } = guide;
    const elementAnchor = stepsElementSelectionTypeToString(steps);

    window.pendo.track(name, {
        appId,
        guideId: id,
        createdAt,
        lastUpdatedAt,
        fromState,
        toState,
        p2: steps[0].type === 'building-block',
        stepCount: steps.length,
        scheduled: !!showsAfter,
        elementAnchor,
    });
}

export function trackIdeaPollPublish (guide) {
    const { appId, segment, id, showsAfter, steps, publishedAt, launchMethod, audienceUiHint, createdAt } = guide;

    try {
        const isSegment =
            (segment && segment.name !== 'Everyone') ||
            (audienceUiHint?.filters &&
                audienceUiHint.filters.length > 0 &&
                audienceUiHint.filters[0].segmentId !== 'everyone');

        window.pendo.track(IDEA_TEST_PUBLISHED, {
            appId,
            createdAt,
            guideId: id,
            ideaCount: steps.length - 2,
            scheduled: !!showsAfter,
            segment: !!isSegment,
            activation: launchMethod,
            isPageTargeted: !!steps[0].pageId,
            publishedAt,
        });
    } catch {
        // Track event didn't submit, but lets carry on
    }
}

export function stepsElementSelectionTypeToString (steps) {
    const stepsArrayString = steps
        .map((step) => {
            const elementSelectionType = get(step, 'attributes.elementSelectionType');
            const stepString = elementSelectionType ? ` Element: ${capitalize(elementSelectionType)}` : ' None';

            return stepString;
        })
        .join()
        .trim();

    return `(${stepsArrayString})`;
}

export function trackGuideExperimentStarted (guide, name) {
    const { groupSize, notificationDuration, startTime } = guide.experiment;
    const hasActiveExperiment = startTime > 0 && startTime < Date.now();

    window.pendo.track(name, {
        experimentPresent: hasActiveExperiment,
        notificationDuration,
        groupSize,
        conversionType: guide.conversion.itemType,
    });
}

export function trackEmbeddedPollGuidePublished (guide) {
    const { id, appId, createdAt, lastUpdatedAt, attributes } = guide;

    window.pendo.track(EMBEDDED_POLL_GUIDE_PUBLISHED, {
        guideId: id,
        appId,
        pollCount: getVisiblePolls(guide).length,
        restartOnReload: !!attributes.restartOnReload,
        createdAt,
        lastUpdatedAt,
    });
}

function trackPublicGuideControlledRollout (guide, justPublished) {
    const { id, appId, createdAt, lastUpdatedAt, rollout } = guide;

    window.pendo.track(PUBLIC_GUIDE_CONTROLLED_ROLLOUT, {
        guideId: id,
        appId,
        createdAt,
        lastUpdatedAt,
        value: rollout?.currentPercentage,
        just_published: justPublished,
    });
}
